import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { elearningAPI } from '../../services/elearningApi';
import { Certification } from '../../types/elearning';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import ErrorState from '../../components/ui/ErrorState';
import EmptyState from '../../components/ui/EmptyState';
import Breadcrumb from '../../components/layout/Breadcrumb';
import { usePageTitle } from '../../hooks/usePageTitle';

function formatIssuedDate(value: string) {
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export default function Certifications() {
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  usePageTitle('Certificates');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        setCertifications(await elearningAPI.fetchCertifications());
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load certificates'));
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Breadcrumb
        items={[
          { label: 'Home', to: '/' },
          { label: 'eLearning', to: '/elearning' },
          { label: 'Certificates' },
        ]}
      />
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Certificates</h1>
        <p className="text-gray-600">Courses you have completed.</p>
      </div>

      {loading ? (
        <LoadingSkeleton lines={6} />
      ) : error ? (
        <ErrorState error={error} onRetry={() => window.location.reload()} />
      ) : certifications.length === 0 ? (
        <EmptyState
          title="No certificates yet"
          description="Finish a course to earn a certificate."
        />
      ) : (
        <div className="space-y-4">
          {certifications.map((certification) => (
            <Card key={certification.id}>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {certification.course?.slug ? (
                      <Link
                        to={`/elearning/courses/${certification.course.slug}`}
                        className="hover:text-primary transition-colors"
                      >
                        {certification.course.title}
                      </Link>
                    ) : (
                      certification.course?.title || 'Completed course'
                    )}
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Issued {formatIssuedDate(certification.issuedAt)}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">{certification.certificateNumber}</p>
                </div>
                <Button variant="secondary" onClick={() => window.print()}>
                  Print
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
